import { AxiosError } from 'axios'
import { Add } from '@mui/icons-material'
import { Box, Button, SxProps } from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import { useCreateVendor } from '@/services/hooks/relay/dynamic'
import { LiveListEntity } from '@/types/live-lists'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import { useDocumentContext } from '@/components/image-zoom-pan/providers/DocumentProvider'
import { useCredentialTypeContext } from '@/components/integration-settings/CredentialTypeProvider'
import NewVendorDialog from './NewVendorDialog'

type NewVendorButtonProps = {
  onCreate: (newVendor: LiveListEntity) => void
  wrapperSx?: SxProps
}

export default function NewVendorButton({
  onCreate,
  wrapperSx,
}: NewVendorButtonProps) {
  const overlay = useOverlay()
  const { document } = useDocumentContext()

  const credentialType = useCredentialTypeContext()

  const { createVendor } = useCreateVendor({
    orgId: document?.owner_org_id || '',
    orgCredentialTypeId: credentialType.id,
    onSuccess: (newVendor) => {
      showSuccessSnackbar(`Vendor Created in ${credentialType.name}`)
      onCreate(newVendor)
    },
    onError: (error) => {
      const { response } = error as AxiosError
      const code = (response?.data as unknown as Record<string, string>)?.code
      let errorMessage = `Failed to add vendor in ${credentialType.name}. Please try again later.`

      if (code === 'duplicate_name') {
        errorMessage = 'A vendor with this name already exists.'
      }

      showErrorSnackbar(errorMessage)
    },
  })

  if (!document) {
    return null
  }

  return (
    // Wrapped with div so button doesn't go fullWidth
    <Box sx={wrapperSx}>
      <Button variant="text" startIcon={<Add />} onClick={overlay.open}>
        New Vendor
      </Button>

      <NewVendorDialog
        credentialType={credentialType}
        overlay={overlay}
        onSave={createVendor}
      />
    </Box>
  )
}
